import React, { useMemo, useState } from 'react';
import Box from '@amzn/meridian/box';
import Text from '@amzn/meridian/text';
import { StackedCapacityBar, StackedSegment } from '../agile/agileComponents/StackedCapacityBar';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';

export interface ZoneCapacity {
  zoneId: string;
  zoneName: string;
  zoneCapacity: number; // # facings x # fixtures
  zoneRation: number; // # fixture / facings
}

export interface ItemCapacityAllocation {
  asin: string;
  zoneId: string;
  zoneName: string;
  category: string;
  capacityAllocated: number;
}

interface CapacityUtilizationProps {
  department: string;
  zonesCapacities: ZoneCapacity[];
  itemCapacityAllocations: ItemCapacityAllocation[];
  deparmentView?: boolean;
  zoneView?: boolean;
}

export const CapacityUtilization: React.FC<CapacityUtilizationProps> = ({
  department,
  zonesCapacities,
  itemCapacityAllocations,
  deparmentView = true,
  zoneView = true,
}) => {
  const [selectedZoneId, setSellectedZoneId] = useState('');

  const selectedZone = useMemo(
    () => zonesCapacities.find((zone) => zone.zoneId === selectedZoneId),
    [selectedZoneId, zonesCapacities],
  );
  const selectedZoneSegments: StackedSegment[] = useMemo(() => {
    return Object.entries(
      itemCapacityAllocations
        .filter((item) => item.zoneId === selectedZoneId)
        .reduce(
          (acc, item) => {
            return {
              ...acc,
              [item.category]: (acc[item.category] || 0) + item.capacityAllocated,
            };
          },
          {} as Record<string, number>,
        ),
    )
      .filter(([subcategoryName]) => subcategoryName !== 'Unassigned')
      .map(([category, totalCapacity]) => ({
        id: category,
        label: category,
        value: totalCapacity,
      }));
  }, [selectedZoneId, itemCapacityAllocations]).sort((a, b) => a.label.localeCompare(b.label));

  const selectedZoneCapacity: number = useMemo(() => {
    return selectedZone?.zoneCapacity ?? 0;
  }, [selectedZone?.zoneCapacity]);

  const zoneToCapacityMap: Map<string, ZoneCapacity> = new Map(zonesCapacities.map((zone) => [zone.zoneId, zone]));

  // Calculate capacity for the department
  const allZonesSegments: StackedSegment[] = Object.entries(
    itemCapacityAllocations.reduce(
      (acc, item) => {
        return {
          ...acc,
          [item.zoneName]:
            (acc[item.zoneName] || 0) + item.capacityAllocated * (zoneToCapacityMap?.get(item.zoneId)?.zoneRation ?? 1),
        };
      },
      {} as Record<string, number>,
    ),
  )
    .filter(([subcategoryName]) => subcategoryName !== 'Unassigned')
    .map(([subcategoryName, totalCapacity]) => ({
      id: subcategoryName,
      label: subcategoryName,
      value: totalCapacity,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const allZonesTotalFixtureCapacity = zonesCapacities.reduce(
    (total, zc) => total + Number(zc.zoneCapacity) * Number(zc.zoneRation),
    0,
  );

  return (
    <div className="capacity-utilization-container">
      <Column width="100%">
        <Box spacingInset={'200'} width="100%">
          <Text type="h400">{department} Zone Overview</Text>
        </Box>

        {deparmentView && (
          <Box>
            <StackedCapacityBar
              key={`capacity-bar-${allZonesSegments.length}-${JSON.stringify(allZonesSegments.map((s) => s.value))}`}
              total={allZonesTotalFixtureCapacity}
              segments={allZonesSegments}
              height={30}
              showLegend={true}
              showValues={true}
              valueText="percent"
              hideValues={true}
              showOverflow={true}
            />
          </Box>
        )}
        {zoneView && (
          <Row>
            <Column width={'100%'}>
              <Box width="100%">
                <Row spacing="200" width="100%">
                  <Text type="h400">Zone: </Text>
                  <Column width="100%">
                    <Select value={selectedZoneId} onChange={setSellectedZoneId} placeholder="Select a zone">
                      {zonesCapacities.map((zc) => (
                        <SelectOption key={zc.zoneId || ''} value={zc.zoneId || ''} label={zc.zoneName || 'unknown'} />
                      ))}
                    </Select>
                  </Column>
                </Row>
              </Box>
              {selectedZoneId !== '' && (
                <>
                  <StackedCapacityBar
                    key={`selected-zone-${selectedZoneId}-${allZonesSegments.length}`}
                    total={selectedZoneCapacity}
                    segments={selectedZoneSegments}
                    height={30}
                    showLegend={true}
                    showValues={true}
                    valueText="percent"
                    hideValues={true}
                    showOverflow={true}
                  />
                  <Row alignmentHorizontal="justify" spacingInset={'200'}>
                    <Text type="b200">
                      Current: {selectedZoneSegments.reduce((sum, row) => sum + row.value, 0)} /{' '}
                      {selectedZone?.zoneCapacity} facings
                    </Text>
                    <Text type="b200">
                      Zone Remaining:{' '}
                      {(selectedZone?.zoneCapacity || 0) -
                        selectedZoneSegments.reduce((sum, row) => sum + row.value, 0)}
                    </Text>
                  </Row>
                </>
              )}
            </Column>
          </Row>
        )}
      </Column>
    </div>
  );
};
